import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-regular-svg-icons";

// Type for a password rule
export type PasswordRule = {
  key: string;
  label: string;
  validate: (
    // FUTURE: Fix false positive eslint warning
    // eslint-disable-next-line no-unused-vars
    password: string
  ) => boolean;
};

// Default password rules
export const defaultPasswordRules: PasswordRule[] = [
  {
    key: "uppercase",
    label: "At least one upper case character (A-Z)",
    validate: (pw) => /[A-Z]/.test(pw),
  },
  {
    key: "lowercase",
    label: "At least one lower case character (a-z)",
    validate: (pw) => /[a-z]/.test(pw),
  },
  {
    key: "digit",
    label: "At least one digit (0-9)",
    validate: (pw) => /\d/.test(pw),
  },
  {
    key: "special",
    label: "At least one special (non-alphanumeric) character",
    validate: (pw) => /[^a-zA-Z0-9]/.test(pw),
  },
  {
    key: "minLength",
    label: "At least 12 characters",
    validate: (pw) => pw.length >= 12,
  },
];

// Custom hook to validate password
export function usePasswordValidation(
  password?: string,
  rules: PasswordRule[] = defaultPasswordRules
) {
  const ruleResults = React.useMemo(() => {
    const ruleResults: Record<string, boolean | undefined> = {};
    for (const rule of rules) {
      ruleResults[rule.key] = password ? rule.validate(password) : undefined;
    }
    return ruleResults;
  }, [password, rules]);

  const isValid = Object.values(ruleResults).every(Boolean);

  return { rules, ruleResults, isValid /*isDirty, onChange*/ };
}

export type PasswordRequirementsFeedbackProps = Omit<
  ReturnType<typeof usePasswordValidation>,
  "isValid"
> & {
  id?: string;
} & React.HTMLAttributes<HTMLDivElement>;

// // define P as props of a react div with the added props of PasswordRequirementsFeedbackProps
// export type P = React.HTMLAttributes<HTMLDivElement> &
//   PasswordRequirementsFeedbackProps;

export const PasswordRequirementsFeedback: React.FC<
  PasswordRequirementsFeedbackProps
> = ({ id, rules, ruleResults = {}, ...props }) => {
  return (
    <div id={id} aria-live="polite" {...props}>
      <div className="fw-semibold">Password requirements:</div>
      <ul style={{ listStyle: "none", padding: 0 }}>
        {rules.map((rule) => {
          const ruleResult = ruleResults[rule.key];
          const { icon, className } =
            typeof ruleResult === "undefined"
              ? {
                  icon: faCircle,
                  className: "text-muted",
                }
              : ruleResult
                ? {
                    icon: faCheckCircle,
                    className: "text-success",
                  }
                : {
                    icon: faTimesCircle,
                    className: "text-danger",
                  };
          return (
            <li
              key={rule.key}
              id={id ?? "" + "_" + rule.key}
              data-testid={id ?? "" + "_" + rule.key}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "0.25em",
              }}
            >
              <span
                style={{
                  marginRight: "0.5em",
                  display: "flex",
                  alignItems: "center",
                }}
                className={className}
              >
                <FontAwesomeIcon icon={icon} style={{ fontSize: "1.2em" }} />
              </span>
              <span>{rule.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
